/**
 * `EX-SEC12` (`docs/27` §9.30): the Safe Exam Browser proof a page inside SEB sends with its API
 * calls.
 *
 * SEB gives the page `window.SafeExamBrowser.security.configKey`: SHA-256 of
 * the page's own URL followed by the exam file's Config Key. The page sends it
 * with that URL (`X-Progsity-Seb-Url`, `X-Progsity-Seb-Key`), and the server
 * checks it against the file it handed out. After a client-side navigation the
 * value is for the old URL until `updateKeys(callback)` has run, and `":"`
 * means not ready yet, so the proof is refreshed whenever the URL changed.
 *
 * Outside SEB there is no `window.SafeExamBrowser` and nothing is sent, so an
 * ordinary browser pays nothing for this.
 */

type SebSecurity = {
  configKey?: string;
  updateKeys?: (callback: () => void) => void;
};

type SebWindow = Window & { SafeExamBrowser?: { security?: SebSecurity } };

/** How long to wait for SEB to refresh its keys before sending what it has. */
const UPDATE_TIMEOUT_MS = 3000;

function security(): SebSecurity | null {
  if (typeof window === "undefined") return null;
  return (window as SebWindow).SafeExamBrowser?.security ?? null;
}

/** True inside Safe Exam Browser (a version with the JavaScript API). */
export function inSafeExamBrowser(): boolean {
  return security() !== null;
}

/** The page's URL as SEB hashes it: absolute, without its fragment. */
function pageUrl(): string {
  const href = window.location.href;
  const hash = href.indexOf("#");
  return hash === -1 ? href : href.slice(0, hash);
}

function readyKey(api: SebSecurity): string | null {
  const key = api.configKey;
  return typeof key === "string" && /^[0-9a-fA-F]{64}$/.test(key) ? key : null;
}

let proven: { url: string; key: string } | null = null;
let refreshing: Promise<void> | null = null;

function refresh(api: SebSecurity): Promise<void> {
  if (!api.updateKeys) return Promise.resolve();
  refreshing ??= new Promise<void>((resolve) => {
    const timer = window.setTimeout(resolve, UPDATE_TIMEOUT_MS);
    try {
      api.updateKeys!(() => {
        window.clearTimeout(timer);
        resolve();
      });
    } catch {
      window.clearTimeout(timer);
      resolve();
    }
  }).finally(() => {
    refreshing = null;
  });
  return refreshing;
}

/**
 * The two headers for a request from this page, or `null` outside SEB or when
 * SEB has no key to give. Never throws: a request without a proof is refused by
 * the server with a page that says what to do, which is the right outcome.
 */
export async function sebProofHeaders(): Promise<Record<string, string> | null> {
  const api = security();
  if (!api) return null;
  const url = pageUrl();
  if (proven?.url !== url) {
    // A page SEB loaded has its key already; after a client-side navigation it
    // is the old page's until SEB refreshes it.
    if (proven !== null || !readyKey(api)) await refresh(api);
    const key = readyKey(api);
    if (!key) return null;
    proven = { url, key };
  }
  return { "X-Progsity-Seb-Url": proven.url, "X-Progsity-Seb-Key": proven.key };
}

/** For tests: forget what was proven. */
export function resetSebProofForTests(): void {
  proven = null;
  refreshing = null;
}
